import AccountTreeRoundedIcon from '@mui/icons-material/AccountTreeRounded';
import { colors } from '../../styleSheet';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import NavigateNextRoundedIcon from '@mui/icons-material/NavigateNextRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import {
  Box,
  Breadcrumbs,
  Chip,
  CircularProgress,
  Drawer,
  Grid,
  Link,
  Typography,
} from '@mui/material';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { SHOWN_OBJECT_IDS } from './Constants';
import { DRAWER, UI } from './tokens';
import { formatDate, prettyName } from './Utils';
import InfoDrawer from './monitoringGraph/InfoDrawer/Drawer';
import MonitoringGraph from './monitoringGraph/MonitoringGraph';
import UtcClock from './UtcClock';


// Compact "LABEL value" pair shown in the session view header navigation summary.
function NavStat({ label, value }) {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        lineHeight: 1.2,
      }}
    >
      <Typography
        sx={{
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: 0.5,
          textTransform: 'uppercase',
          color: UI.textMuted,
        }}
      >
        {label}
      </Typography>
      <Typography sx={{ fontSize: 14, fontWeight: 700, color: UI.textPrimary }}>
        {value}
      </Typography>
    </Box>
  );
}

export default function SessionView(props) {
  const {
    objectNames,
    batchesReceived,
    getObjectNames,
    alerts,
    allAlertPolicies,
    getPPNodes,
    pipelineProgressNodes,
    isLoadingPPNodesInfo,
    navigationOptions,
  } = props;

  const viewId = 'Pipeline-actions';
  // Start with shelf-label objects (1 and 2) selected so the graph renders on load.
  const [selectedObjectIds, setSelectedObjectIds] = useState([1, 2]);
  const { session, date } = useParams();
  const [selectedNode, setSelectedNode] = useState(null);
  const [selectedEdge, setSelectedEdge] = useState(null);
  const [selectedObject, setSelectedObject] = useState(null);
  const [nodes, setNodes] = useState([]);
  const [edges, setEdges] = useState([]);

  useEffect(() => {
    getObjectNames();
  }, [getObjectNames]);

  useEffect(() => {
    let interval60;
    let intervalDelay;
    if (!isLoadingPPNodesInfo && session && date) {
      interval60 = setInterval(() => {
        const canRefresh = date === formatDate(new Date());
        if (!isLoadingPPNodesInfo && canRefresh) {
          handleGetPPNodes(session, { canRefresh });
        }
      }, 3 * 60 * 1000);

      intervalDelay = setTimeout(() => {
        handleGetPPNodes(session)
      }, 100);
    }

    return () => {
      clearInterval(interval60);
      clearInterval(intervalDelay);
    }
  }, [session, date]);

  useEffect(() => {
    if (handleCanShowFlowChart() && pipelineProgressNodes) {
      const nodesEdges = selectedObjectIds.reduce((acc, objectId) => {
        let nodes = pipelineProgressNodes?.[objectId]?.nodes ?? [];
        let edges = pipelineProgressNodes?.[objectId]?.edges ?? [];

        // filter edges by objectIds
        edges = edges.filter(edge => {
          return selectedObjectIds.includes(edge.data.child.object_id);
        });

        return {
          nodes: [...acc.nodes, ...nodes],
          edges: [...acc.edges, ...edges],
        }
      }, {
        nodes: [],
        edges: [],
      });

      const ppNodes = nodesEdges?.nodes ?? [];
      const ppEdges = nodesEdges?.edges ?? [];

      setNodes(ppNodes);
      setEdges(ppEdges);
      setTimeout(() => {
        setNodes([...ppNodes]);
        setEdges([...ppEdges]);
      }, 50);
    }
  }, [selectedObjectIds, isLoadingPPNodesInfo, pipelineProgressNodes]);


  // Handlers
  const handleGetPPNodes = (session, { canRefresh = false } = {}) => {
    let hard_finished_sessions = localStorage.getItem('hard_finished_sessions') ?? '[]';
    if (hard_finished_sessions !== '[]') {
      hard_finished_sessions = JSON.stringify(JSON.parse(hard_finished_sessions)?.filter((session) => moment().diff(session.date, 'days') <= 3));
    }
    localStorage.setItem('hard_finished_sessions', hard_finished_sessions);

    // total_aisles, processes_data and batches_received are no longer pre-fetched and
    // passed in — the backend derives them from Pipeline Manager (/pipeline-status +
    // /process-flow) when absent, so the session view needs only one request.
    const ppNodesForm = canRefresh ? {
      date: date,
      alerts: JSON.stringify(alerts ?? []),
      session: session,
      all_alert_policies: JSON.stringify(allAlertPolicies),
      hard_finished_sessions: hard_finished_sessions,
      chain_code: session.split('-')[1].replace(/[^a-zA-Z]+/g, ''),
      store: session.split('-')[1],
    } : {
      date: date,
      alerts: JSON.stringify(alerts ?? []),
      session: session,
      chain_code: session.split('-')[1].replace(/[^a-zA-Z]+/g, ''),
      store: session.split('-')[1],
    };
    getPPNodes(ppNodesForm);
  }

  const handleRefreshNodes = () => {
    setSelectedObjectIds([...selectedObjectIds]);
  }

  const onNodeClick = (processId, node) => {
    if (node.root) {
      setSelectedObject('root');
      setSelectedNode(node);
    } else {
      setSelectedObject('node');
      setSelectedNode(node);
    }
  };

  const onEdgeClick = (edge) => {
    setSelectedObject('edge');
    setSelectedEdge(edge);
  };

  const toggleSelectedObjectId = (objectId) => {
    const newSelectedObjectIds = new Set(selectedObjectIds);
    if (selectedObjectIds.includes(objectId)) {
      newSelectedObjectIds.delete(objectId);
    } else {
      newSelectedObjectIds.add(objectId);
    }

    setSelectedObjectIds(Array.from(newSelectedObjectIds));
  };

  const handleWidth = () => {
    const windowInnerWidth = window.innerWidth;
    return `calc(${windowInnerWidth}px - 20em)`;
  }

  const handleCanShowFlowChart = () => {
    return !isLoadingPPNodesInfo || Object.values(pipelineProgressNodes ?? {}).length > 0;
  }

  // Session-level navigation summary (sent under the reserved "navigation" key).
  const navigation = pipelineProgressNodes?.navigation;
  const formatNavTime = (value) =>
    value ? moment(value).format('DD/MM HH:mm') : '—';

  return (
    <Grid container>
      <Grid item xs={12}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2,
          }}
        >
          <Breadcrumbs
            aria-label="breadcrumb"
            separator={
              <NavigateNextRoundedIcon sx={{ fontSize: 18, color: UI.textMuted }} />
            }
            sx={{ '& .MuiBreadcrumbs-li': { display: 'flex', alignItems: 'center' } }}
          >
            <Link
              underline="none"
              href="/pipeline_processing"
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.625,
                fontSize: 14,
                fontWeight: 500,
                color: UI.textSecondary,
                transition: 'color .15s ease',
                '&:hover': { color: UI.textPrimary },
              }}
            >
              <AccountTreeRoundedIcon sx={{ fontSize: 17 }} />
              Pipeline Monitoring
            </Link>
            <Typography
              sx={{
                fontSize: 14,
                fontWeight: 700,
                color: UI.textPrimary,
                px: 1,
                py: 0.25,
                borderRadius: UI.radiusButton,
                backgroundColor: colors.slate100,
              }}
            >
              {session}
            </Typography>
          </Breadcrumbs>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5 }}>
            {navigation && (
              <>
                <NavStat
                  label="Planned Aisles"
                  value={`${navigation.plannedAisles}`}
                />
                <NavStat label="Nav start" value={formatNavTime(navigation.navStart)} />
                <NavStat label="Nav finish" value={formatNavTime(navigation.navFinish)} />
              </>
            )}
            <UtcClock />
          </Box>
        </Box>
      </Grid>
      {!handleCanShowFlowChart() ? (
        <Grid item xs={12}>
          <CircularProgress />
        </Grid>
      ) : (
        <Grid
          container
          item
          xs={12}
          width="100%"
          padding="0"
          spacing="10px"
          flexDirection={'column'}
          sx={{
            borderRadius: '15px',
            background: colors.slate100,
            pb: 2,
            px: 2,
            display: 'block',
            boxShadow: 1,
            width: handleWidth(),
          }}
        >
          <Grid item xs={12}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              overflow: 'auto',
              my: 1,
              pb: 1.5,
            }}
          >
            <Box
              sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}
            >
              {SHOWN_OBJECT_IDS.map((objectId) => {
                const selected = selectedObjectIds.includes(objectId);
                return (
                  <Chip
                    key={objectId}
                    variant={selected ? 'filled' : 'outlined'}
                    color="primary"
                    icon={selected ? <CheckRoundedIcon /> : undefined}
                    label={prettyName(objectNames[objectId])}
                    onClick={() => toggleSelectedObjectId(objectId)}
                    sx={{
                      height: 32,
                      fontSize: 13,
                      fontWeight: 600,
                      borderRadius: UI.radiusPill,
                      transition: 'box-shadow .15s ease, background-color .15s ease',
                      '& .MuiChip-icon': { fontSize: 16, ml: '6px' },
                      ...(selected
                        ? { boxShadow: '0 2px 6px rgba(26,36,45,0.22)' }
                        : {
                            color: UI.textSecondary,
                            borderColor: UI.border,
                            backgroundColor: UI.surface,
                          }),
                      '&:hover': selected
                        ? { boxShadow: '0 4px 12px rgba(26,36,45,0.28)' }
                        : { backgroundColor: UI.surfaceMuted },
                    }}
                  />
                );
              })}
              <Chip
                label="Refresh"
                variant="outlined"
                icon={<RefreshRoundedIcon />}
                onClick={handleRefreshNodes}
                sx={{
                  ml: 'auto',
                  height: 32,
                  fontSize: 13,
                  fontWeight: 600,
                  borderRadius: UI.radiusPill,
                  color: UI.textSecondary,
                  borderColor: UI.border,
                  '& .MuiChip-icon': { fontSize: 16, color: UI.textSecondary },
                  transition: 'background-color .15s ease',
                  '&:hover': { backgroundColor: UI.surfaceMuted },
                }}
              />
            </Box>
            {selectedObjectIds?.length === 0 ? (
              <Typography
                sx={{ marginLeft: 1, marginTop: '6px', fontSize: '12px', color: UI.textMuted }}
              >
                Select any of the chips above to display the pipeline graph
              </Typography>
            ) : null}
          </Grid>

          {nodes?.length ?
            <Grid
              item
              xs={12}
              sx={{
                display: 'block',
                overflow: 'auto',
                borderRadius: '7px',
                padding: '0 !important',
                border: `1px solid ${colors.lavenderTint}`,
              }}
            >
              <MonitoringGraph
                nodes={nodes}
                edges={edges}
                onNodeClick={onNodeClick}
                onEdgeClick={onEdgeClick}
              />
              <Drawer
                PaperProps={{
                  sx: {
                    backgroundColor: DRAWER.surface,
                    maxWidth: '500px',
                    minWidth: '500px',
                  },
                }}
                anchor={'right'}
                open={selectedNode || selectedEdge ? true : false}
                onClose={() => {
                  setSelectedObject(undefined);
                  setSelectedNode(undefined);
                  setSelectedEdge(undefined);
                }}
              >
                {' '}
                {selectedNode || selectedEdge ? (
                  <InfoDrawer
                    object={selectedObject}
                    node={selectedNode}
                    edge={selectedEdge}
                    navOptions={navigationOptions}
                    viewId={viewId}
                  />
                ) : null}{' '}
              </Drawer>{' '}
            </Grid>
            :
            <Grid
              item
              xs={12}
              sx={{
                mx: '1em',
                overflow: 'auto',
                display: 'block',
                minHeight: '10em',
                borderRadius: '7px',
                border: `1px solid ${colors.lavenderTint}`,
              }}
            >
            </Grid>
          }
        </Grid>
      )}
    </Grid>
  );
}
